import type {
  CampaignReportResponse,
  CampaignSummary,
  LiftMetrics,
  ProductSummary,
} from "../../types";
import {
  formatReportDateLabel,
  sortByReportDate,
} from "../../utils/dateFormatting";
import AjinomotoChickenPack from "../../logos/AJI-Chicken-Hane-Gyoza-Dumplings_5c8845f0-03a7-46cb-af14-03de74c74653.56e3202407daf4d55e87826351a9a44e.jpeg";
import AjinomotoPorkPack from "../../logos/AJI-Pork-Hane-Gyoza-Dumplings_df1952c6-e9cc-40f0-897d-2483f84bc6fc.f118d18f7fc26332f71d8be9d5d915cf.jpeg";

export const ROAS_THRESHOLDS = {
  EXCELLENT: 3,
  GOOD: 2,
  TARGET: 2,
} as const;

const SALES_FORMAT_THRESHOLDS = {
  MILLION: 1000000,
  THOUSAND: 10000,
  THOUSAND_DIVISOR: 1000,
} as const;

const IMPRESSIONS_FORMAT_THRESHOLD = 1000000;

const CHICKEN_COLOR = "#21409A";
const PORK_COLOR = "#AC3E97";
const CHICKEN_IMAGE = AjinomotoChickenPack;
const PORK_IMAGE = AjinomotoPorkPack;
const FALLBACK_PRODUCT_COLORS = [CHICKEN_COLOR, PORK_COLOR];
const FALLBACK_PRODUCT_IMAGES = [CHICKEN_IMAGE, PORK_IMAGE];

export interface AjinomotoProductCardViewModel extends ProductSummary {
  accentColor: string;
  productImage: string;
  roasValue: number;
  ctr: number;
  addToCartRate: number;
  totalSalesValue: number;
  inStoreSalesValue: number;
  shortLabel: string;
  distributionLabel: string;
}

export interface AjinomotoLiftSummary {
  baselinePeriodDays: number;
  liftPeriodDays: number;
  baselineAvgSales: number;
  liftAvgSales: number;
  baselineAvgUnits: number;
  liftAvgUnits: number;
  avgSalesLiftPercentage: number | null;
  avgUnitsLiftPercentage: number | null;
}

export interface AjinomotoDailyTrendDatum {
  label: string;
  sales: number;
  spend: number;
}

export interface AjinomotoCampaignRowViewModel extends CampaignSummary {
  roasValue: number;
  totalSalesValue: number;
  totalSpendValue: number;
}

export interface AjinomotoLayoutViewModel {
  avgDailySales: number;
  avgDailySpend: number;
  averageRoas: number;
  productCards: AjinomotoProductCardViewModel[];
  productColors: string[];
  liftSummary: AjinomotoLiftSummary | null;
  dailyTrendData: AjinomotoDailyTrendDatum[];
  totalAddToCart: number;
  totalInStoreUnits: number;
  campaignRows: AjinomotoCampaignRowViewModel[];
}

export const formatSales = (sales: string | number): string => {
  const value = typeof sales === "string" ? parseFloat(sales) : sales;
  if (value >= SALES_FORMAT_THRESHOLDS.MILLION) {
    return `$${Math.ceil(value / SALES_FORMAT_THRESHOLDS.MILLION)}M`;
  }
  if (value >= SALES_FORMAT_THRESHOLDS.THOUSAND) {
    return `$${Math.ceil(value / SALES_FORMAT_THRESHOLDS.THOUSAND_DIVISOR)}k`;
  }
  return `$${Math.ceil(value).toLocaleString()}`;
};

export const formatImpressions = (impressions: number): string => {
  if (impressions >= IMPRESSIONS_FORMAT_THRESHOLD) {
    return `${(impressions / IMPRESSIONS_FORMAT_THRESHOLD).toFixed(1)}M`;
  }
  return `${(impressions / SALES_FORMAT_THRESHOLDS.THOUSAND_DIVISOR).toFixed(1)}k`;
};

export const formatRoasBadge = (roas: number): string => {
  const fractionDigits = roas >= 10 ? 1 : 2;
  return `${roas.toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}×`;
};

export const formatCurrencyValue = (
  value: number,
  minimumFractionDigits = 2,
  maximumFractionDigits = minimumFractionDigits,
): string =>
  value.toLocaleString("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
  });

export const formatAverageUnits = (value: number): string =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: value >= 100 ? 0 : 1,
    maximumFractionDigits: value >= 100 ? 0 : 1,
  });

export const formatLiftPercent = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) {
    return "N/A";
  }
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${value.toFixed(1)}%`;
};

const safeParseNumber = (value: string | undefined): number => {
  if (!value) {
    return 0;
  }
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const calculatePeriodDays = (start: string, end: string): number => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (
    Number.isNaN(startDate.getTime()) ||
    Number.isNaN(endDate.getTime()) ||
    endDate < startDate
  ) {
    return 1;
  }
  const diffMs = endDate.getTime() - startDate.getTime();
  return Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1);
};

const resolveProductColor = (productName: string, index: number): string => {
  const normalizedName = productName.toLowerCase();
  if (normalizedName.includes("chicken")) {
    return CHICKEN_COLOR;
  }
  if (normalizedName.includes("pork")) {
    return PORK_COLOR;
  }
  return FALLBACK_PRODUCT_COLORS[index % FALLBACK_PRODUCT_COLORS.length];
};

const resolveProductImage = (productName: string, index: number): string => {
  const normalizedName = productName.toLowerCase();
  if (normalizedName.includes("chicken")) {
    return CHICKEN_IMAGE;
  }
  if (normalizedName.includes("pork")) {
    return PORK_IMAGE;
  }
  return FALLBACK_PRODUCT_IMAGES[index % FALLBACK_PRODUCT_IMAGES.length];
};

const buildProductCards = (
  products: ProductSummary[] | undefined,
): AjinomotoProductCardViewModel[] =>
  (products ?? []).map((product, index) => {
    const roasValue = parseFloat(product.roas);
    const ctr =
      product.total_impressions > 0
        ? (product.total_clicks / product.total_impressions) * 100
        : 0;
    const addToCartRate =
      product.total_clicks > 0
        ? (product.add_to_cart / product.total_clicks) * 100
        : 0;

    return {
      ...product,
      accentColor: resolveProductColor(product.product_name, index),
      productImage: resolveProductImage(product.product_name, index),
      roasValue,
      ctr,
      addToCartRate,
      totalSalesValue: parseFloat(product.total_sales),
      inStoreSalesValue: parseFloat(product.in_store_sales),
      shortLabel: product.product_name.replace("Ajinomoto ", "").split(" ")[0],
      distributionLabel: product.product_name
        .replace("Ajinomoto ", "")
        .split(",")[0],
    };
  });

const buildLiftSummary = (
  liftMetrics: LiftMetrics | undefined,
): AjinomotoLiftSummary | null => {
  if (!liftMetrics) {
    return null;
  }

  const baselinePeriodDays = calculatePeriodDays(
    liftMetrics.baseline_period_start,
    liftMetrics.baseline_period_end,
  );
  const liftPeriodDays = calculatePeriodDays(
    liftMetrics.lift_period_start,
    liftMetrics.lift_period_end,
  );

  const baselineAvgSales = safeParseNumber(liftMetrics.baseline_daily_avg_sales);
  const liftAvgSales = safeParseNumber(liftMetrics.lift_daily_avg_sales);
  const baselineAvgUnits = liftMetrics.baseline_units / baselinePeriodDays;
  const liftAvgUnits = liftMetrics.lift_units / liftPeriodDays;

  return {
    baselinePeriodDays,
    liftPeriodDays,
    baselineAvgSales,
    liftAvgSales,
    baselineAvgUnits,
    liftAvgUnits,
    avgSalesLiftPercentage:
      baselineAvgSales > 0
        ? ((liftAvgSales - baselineAvgSales) / baselineAvgSales) * 100
        : null,
    avgUnitsLiftPercentage:
      baselineAvgUnits > 0
        ? ((liftAvgUnits - baselineAvgUnits) / baselineAvgUnits) * 100
        : null,
  };
};

const buildDailyTrendData = (
  dailyMetrics: CampaignReportResponse["daily_metrics"],
): AjinomotoDailyTrendDatum[] =>
  sortByReportDate(dailyMetrics, (metric) => metric.report_date)
    .map((metric) => ({
      label: formatReportDateLabel(metric.report_date),
      sales: parseFloat(metric.total_sales),
      spend: parseFloat(metric.total_spend),
    }));

const buildCampaignRows = (
  campaigns: CampaignSummary[] | undefined,
): AjinomotoCampaignRowViewModel[] =>
  [...(campaigns ?? [])]
    .sort((a, b) => parseFloat(b.total_sales) - parseFloat(a.total_sales))
    .map((campaign) => ({
      ...campaign,
      roasValue: parseFloat(campaign.roas),
      totalSalesValue: parseFloat(campaign.total_sales),
      totalSpendValue: parseFloat(campaign.total_spend),
    }));

export const buildAjinomotoLayoutViewModel = (
  data: CampaignReportResponse,
): AjinomotoLayoutViewModel => {
  const avgDailySales =
    data.summary.total_days > 0
      ? parseFloat(data.summary.total_sales) / data.summary.total_days
      : 0;
  const avgDailySpend =
    data.summary.total_days > 0
      ? parseFloat(data.summary.total_spend) / data.summary.total_days
      : 0;
  const productCards = buildProductCards(data.products);

  return {
    avgDailySales,
    avgDailySpend,
    averageRoas: parseFloat(data.summary.average_roas),
    productCards,
    productColors: productCards.map((product) => product.accentColor),
    liftSummary: buildLiftSummary(data.lift_metrics),
    dailyTrendData: buildDailyTrendData(data.daily_metrics),
    totalAddToCart: productCards.reduce(
      (sum, product) => sum + product.add_to_cart,
      0,
    ),
    totalInStoreUnits: productCards.reduce(
      (sum, product) => sum + product.in_store_units,
      0,
    ),
    campaignRows: buildCampaignRows(data.campaigns),
  };
};
